import { defaultOpportunityFilters, type OpportunityFilters } from './types';

const filterStorageKey = 'business-radar:filters:v1';
const workspaceStorageKey = 'business-radar:workspace:v1';
const maxStoredOpportunityIds = 500;

export type OpportunityWorkspaceState = Readonly<{
  watchedIds: readonly string[];
  hiddenIds: readonly string[];
  lastSeenAt: string | null;
}>;

const defaultWorkspaceState: OpportunityWorkspaceState = {
  watchedIds: [],
  hiddenIds: [],
  lastSeenAt: null,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeOpportunityIds(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  const ids = value.filter((item): item is string => typeof item === 'string' && item.length > 0);
  return [...new Set(ids)].slice(0, maxStoredOpportunityIds);
}

export function loadOpportunityFilters(): OpportunityFilters {
  if (typeof window === 'undefined') return defaultOpportunityFilters;
  try {
    const raw = window.localStorage.getItem(filterStorageKey);
    if (!raw) return defaultOpportunityFilters;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return defaultOpportunityFilters;

    return {
      keyword: typeof parsed.keyword === 'string' ? parsed.keyword : '',
      region: typeof parsed.region === 'string' ? parsed.region : '',
      workType: parsed.workType === 'SERVICE' || parsed.workType === 'CONSTRUCTION' ? parsed.workType : 'ALL',
      stage: parsed.stage === 'PLAN' || parsed.stage === 'PRESPEC' || parsed.stage === 'BID' ? parsed.stage : 'ALL',
      minBudget: typeof parsed.minBudget === 'number' && Number.isFinite(parsed.minBudget) ? parsed.minBudget : null,
      closingWithinDays: typeof parsed.closingWithinDays === 'number' && Number.isFinite(parsed.closingWithinDays)
        ? parsed.closingWithinDays
        : 30,
      sort: parsed.sort === 'CLOSING_SOON' || parsed.sort === 'BUDGET_DESC' ? parsed.sort : 'NEWEST',
    };
  } catch {
    return defaultOpportunityFilters;
  }
}

export function saveOpportunityFilters(filters: OpportunityFilters): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(filterStorageKey, JSON.stringify(filters));
  } catch {
    // Local persistence must never block the Radar workflow.
  }
}

export function clearOpportunityFilters(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(filterStorageKey);
  } catch {
    // Ignore storage failures and keep the in-memory workflow usable.
  }
}

export function loadOpportunityWorkspace(): OpportunityWorkspaceState {
  if (typeof window === 'undefined') return defaultWorkspaceState;
  try {
    const raw = window.localStorage.getItem(workspaceStorageKey);
    if (!raw) return defaultWorkspaceState;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return defaultWorkspaceState;

    return {
      watchedIds: normalizeOpportunityIds(parsed.watchedIds),
      hiddenIds: normalizeOpportunityIds(parsed.hiddenIds),
      lastSeenAt: typeof parsed.lastSeenAt === 'string' ? parsed.lastSeenAt : null,
    };
  } catch {
    return defaultWorkspaceState;
  }
}

export function saveOpportunityWorkspace(state: OpportunityWorkspaceState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(workspaceStorageKey, JSON.stringify({
      watchedIds: normalizeOpportunityIds(state.watchedIds),
      hiddenIds: normalizeOpportunityIds(state.hiddenIds),
      lastSeenAt: state.lastSeenAt,
    }));
  } catch {
    // Local persistence must never block the Radar workflow.
  }
}
